import { useState } from 'react';
import { dataLayer } from '../../data/dataLayer';
import type { ModeId } from '../shared/modeTheme';
import type { FamilyPickerMode } from '../FamilyPicker';
export interface StartModeConfig {
  targetFamilyId?: string;
  targetModules?: string[];
}
interface UseModeFlowProps {
  onStartMode?: (modeId: string, config?: StartModeConfig) => void;
  newlyUnlockedFamilyId: string | null;
}
export function useModeFlow({
  onStartMode,
  newlyUnlockedFamilyId
}: UseModeFlowProps) {
  const [familyPickerOpen, setFamilyPickerOpen] = useState(false);
  const [familyPickerMode, setFamilyPickerMode] = useState<FamilyPickerMode>('sprint');
  const [pendingModeId, setPendingModeId] = useState<string | null>(null);
  const [challengeModalOpen, setChallengeModalOpen] = useState(false);
  const [pendingChallengeFamilyId, setPendingChallengeFamilyId] = useState<string | null>(null);
  const [introSheetMode, setIntroSheetMode] = useState<ModeId | null>(null);
  const [pendingIntroConfig, setPendingIntroConfig] = useState<{
    familyId?: string;
  } | null>(null);
  const [fluencyModulePickerOpen, setFluencyModulePickerOpen] = useState(false);
  const proceedWithSprint = (familyId?: string) => {
    if (familyId && onStartMode) {
      onStartMode('sprint', {
        targetFamilyId: familyId
      });
      return;
    }
    setPendingModeId('sprint');
    setFamilyPickerMode('sprint');
    setFamilyPickerOpen(true);
  };
  const proceedWithFreePractice = (familyId?: string) => {
    if (familyId && onStartMode) {
      onStartMode('free', {
        targetFamilyId: familyId
      });
      return;
    }
    setPendingModeId('free');
    setFamilyPickerMode('free');
    setFamilyPickerOpen(true);
  };
  const proceedWithChallenge = (familyId?: string) => {
    if (familyId && onStartMode) {
      setPendingChallengeFamilyId(familyId);
      setChallengeModalOpen(true);
      return;
    }
    setPendingModeId('challenge');
    setFamilyPickerMode('challenge');
    setFamilyPickerOpen(true);
  };
  const handleModeClick = (modeId: string, familyId?: string) => {
    if (modeId === 'fluency') {
      dataLayer.getFluencyIntroSeen().then(seen => {
        if (!seen) {
          setPendingIntroConfig({
            familyId
          });
          setIntroSheetMode('fluency');
        } else {
          if (familyId && onStartMode) {
            onStartMode('fluency', {
              targetFamilyId: familyId
            });
          } else {
            setFluencyModulePickerOpen(true);
          }
        }
      }).catch(() => {});
      return;
    }
    if (modeId === 'sprint') {
      dataLayer.getSprintIntroSeen().then(seen => {
        if (!seen) {
          setPendingIntroConfig({
            familyId
          });
          setIntroSheetMode('sprint');
        } else {
          proceedWithSprint(familyId);
        }
      }).catch(() => {});
      return;
    }
    if (modeId === 'free') {
      dataLayer.getFreePracticeIntroSeen().then(seen => {
        if (!seen) {
          setPendingIntroConfig({
            familyId
          });
          setIntroSheetMode('free');
        } else {
          proceedWithFreePractice(familyId);
        }
      }).catch(() => {});
      return;
    }
    if (modeId === 'challenge') {
      dataLayer.getChallengeIntroSeen().then(seen => {
        if (!seen) {
          setPendingIntroConfig({
            familyId
          });
          setIntroSheetMode('challenge');
        } else {
          proceedWithChallenge(familyId);
        }
      }).catch(() => {});
      return;
    }
    if (familyId && onStartMode) {
      onStartMode(modeId, {
        targetFamilyId: familyId
      });
      return;
    }
    onStartMode?.(modeId);
  };
  const handleNewlyUnlockedClick = () => {
    if (newlyUnlockedFamilyId) {
      dataLayer.setChallengeIntroSeen(true);
      setPendingChallengeFamilyId(newlyUnlockedFamilyId);
      setChallengeModalOpen(true);
    }
  };
  const handleFamilySelect = (familyId: string) => {
    setFamilyPickerOpen(false);
    if (pendingModeId === 'challenge') {
      setPendingChallengeFamilyId(familyId);
      setChallengeModalOpen(true);
      setPendingModeId(null);
      return;
    }
    if (onStartMode && pendingModeId) {
      onStartMode(pendingModeId, {
        targetFamilyId: familyId
      });
    }
    setPendingModeId(null);
  };
  const handleFamilyPickerClose = () => {
    setFamilyPickerOpen(false);
    setPendingModeId(null);
  };
  const handleChallengeAccept = () => {
    setChallengeModalOpen(false);
    if (onStartMode && pendingChallengeFamilyId) {
      onStartMode('challenge', {
        targetFamilyId: pendingChallengeFamilyId
      });
    }
    setPendingChallengeFamilyId(null);
  };
  const handleChallengeDecline = () => {
    setChallengeModalOpen(false);
    setPendingChallengeFamilyId(null);
  };
  const handleIntroStart = () => {
    if (!introSheetMode) return;
    const modeId = introSheetMode;
    const familyId = pendingIntroConfig?.familyId;
    setIntroSheetMode(null);
    setPendingIntroConfig(null);
    if (modeId === 'fluency') {
      dataLayer.setFluencyIntroSeen(true);
      if (familyId && onStartMode) {
        onStartMode('fluency', {
          targetFamilyId: familyId
        });
      } else {
        setFluencyModulePickerOpen(true);
      }
    } else if (modeId === 'sprint') {
      dataLayer.setSprintIntroSeen(true);
      proceedWithSprint(familyId);
    } else if (modeId === 'free') {
      dataLayer.setFreePracticeIntroSeen(true);
      proceedWithFreePractice(familyId);
    } else if (modeId === 'challenge') {
      dataLayer.setChallengeIntroSeen(true);
      proceedWithChallenge(familyId);
    }
  };
  const handleIntroCancel = () => {
    setIntroSheetMode(null);
    setPendingIntroConfig(null);
  };
  return {
    familyPickerOpen,
    familyPickerMode,
    challengeModalOpen,
    pendingChallengeFamilyId,
    introSheetMode,
    fluencyModulePickerOpen,
    handleModeClick,
    handleNewlyUnlockedClick,
    handleFamilySelect,
    handleFamilyPickerClose,
    handleChallengeAccept,
    handleChallengeDecline,
    handleIntroStart,
    handleIntroCancel,
    setFluencyModulePickerOpen
  };
}